import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Loader2, ShieldAlert, CheckCircle2, XCircle, Clock, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { AppShell } from "@/components/AppShell";
import { OrgTabs } from "@/components/OrgTabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useOrg } from "@/hooks/useOrg";
import { api, apiErr } from "@/lib/api";

export default function OrgQueue() {
  const { slug } = useParams();
  const { org, reload: reloadOrg } = useOrg(slug);
  const [queue, setQueue] = useState(null);
  const [notes, setNotes] = useState({}); // versionId -> note text (reject reason / override note)
  const [overrides, setOverrides] = useState({}); // versionId -> bool
  const isAdmin = org && (org.role === "owner" || org.role === "admin");

  const load = useCallback(async () => {
    if (!slug) return;
    try {
      const { data } = await api.get(`/orgs/${slug}/queue`);
      setQueue(data);
    } catch (e) {
      setQueue([]);
    }
  }, [slug]);

  useEffect(() => {
    load();
  }, [load]);

  const setNote = (id, v) => setNotes((n) => ({ ...n, [id]: v }));
  const toggleOverride = (id) => setOverrides((o) => ({ ...o, [id]: !o[id] }));

  const approve = async (v) => {
    try {
      await api.post(`/orgs/${slug}/versions/${v.id}/approve`, { override: !!overrides[v.id], note: notes[v.id] });
      toast.success(`Published — ${v.slug} v${v.version} is now live in the registry`);
      load();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : d?.message || "Approve failed");
    }
  };

  const reject = async (v) => {
    try {
      await api.post(`/orgs/${slug}/versions/${v.id}/reject`, { note: notes[v.id] });
      toast.success(`Rejected ${v.slug} v${v.version}`);
      load();
    } catch (e) {
      toast.error(apiErr(e, "Reject failed"));
    }
  };

  const setApprovalMode = async (mode) => {
    try {
      await api.patch(`/orgs/${slug}/settings`, { approval_mode: mode });
      toast.success(mode === "auto_clean" ? "Clean scans now auto-publish" : "Everything now requires manual approval");
      reloadOrg();
    } catch (e) {
      toast.error("Could not update approval mode");
    }
  };

  return (
    <AppShell>
      <OrgTabs slug={slug} />
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <ShieldAlert className="h-5 w-5 text-[color:var(--rb-accent)]" />
          <h1 className="rb-display text-2xl font-semibold tracking-tight text-[color:var(--rb-text)]">
            Approval queue{org ? ` — ${org.name}` : ""}
          </h1>
        </div>
        {isAdmin && org && (
          <div className="inline-flex gap-1 rounded-xl border border-[color:var(--rb-border)] bg-[color:var(--rb-code)] p-1" data-testid="approval-mode-toggle">
            {[["manual", "Manual"], ["auto_clean", "Auto-approve clean"]].map(([mode, label]) => (
              <button key={mode} onClick={() => setApprovalMode(mode)} data-testid={`approval-mode-${mode}`}
                className={`rounded-lg px-3 py-1.5 text-xs font-medium transition-colors ${
                  org.approval_mode === mode ? "bg-[color:rgba(45,212,191,0.14)] text-[color:var(--rb-accent)]" : "text-[color:var(--rb-text-dim)] hover:text-[color:var(--rb-text)]"
                }`}>
                {label}
              </button>
            ))}
          </div>
        )}
      </div>
      <p className="mt-1 text-sm text-[color:var(--rb-text-faint)]">
        Nothing reaches the plugin registry until a version is approved here — every submission is safety-scanned.
      </p>

      {org && !isAdmin ? (
        <div className="mt-8 text-center text-sm text-[color:var(--rb-text-faint)]">Admin access required.</div>
      ) : queue === null ? (
        <div className="mt-8 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-[color:var(--rb-text-faint)]" /></div>
      ) : queue.length === 0 ? (
        <div className="mt-8 rounded-2xl border border-dashed border-[color:var(--rb-border)] py-16 text-center text-sm text-[color:var(--rb-text-faint)]" data-testid="org-queue-empty">
          Nothing waiting on review.
        </div>
      ) : (
        <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2" data-testid="org-queue-grid">
          {queue.map((v) => {
            const reasons = v.scan?.reasons || [];
            const verdict = v.scan?.llm?.verdict;
            return (
              <div key={v.id} className="rounded-2xl border border-[color:var(--rb-border)] bg-[color:var(--rb-obsidian)] p-4" data-testid="org-queue-item">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="rb-mono truncate text-sm font-semibold text-[color:var(--rb-text)]">{v.slug} <span className="text-[color:var(--rb-text-faint)]">v{v.version}</span></div>
                    <div className="text-xs text-[color:var(--rb-text-faint)]">by @{v.creator_name} · {v.kind}</div>
                  </div>
                  {v.flagged ? (
                    <span className="flex shrink-0 items-center gap-1 rounded-full bg-[color:rgba(248,113,113,0.14)] px-2 py-0.5 text-xs text-[color:#f87171]">
                      <AlertTriangle className="h-3 w-3" /> Flagged
                    </span>
                  ) : (
                    <span className="flex shrink-0 items-center gap-1 rounded-full bg-[color:var(--rb-gold-soft)] px-2 py-0.5 text-xs text-[color:var(--rb-gold)]">
                      <Clock className="h-3 w-3" /> Pending
                    </span>
                  )}
                </div>
                {v.changelog && <p className="mt-2 text-sm text-[color:var(--rb-text-dim)]">{v.changelog}</p>}
                {reasons.length > 0 && (
                  <div className="mt-3 rounded-xl border border-[color:var(--rb-border)] bg-[color:var(--rb-surface)] p-2.5">
                    <div className="flex items-center gap-2 text-xs">
                      <span className="text-[color:var(--rb-text-faint)]">AI verdict:</span>
                      <span className={verdict === "malicious" ? "text-[color:#f87171]" : verdict === "safe" ? "text-[color:var(--rb-accent)]" : "text-[color:var(--rb-gold)]"}>{verdict || "n/a"}</span>
                    </div>
                    <ul className="mt-1.5 list-inside list-disc text-[11px] text-[color:var(--rb-text-faint)]">
                      {reasons.slice(0, 5).map((r, i) => <li key={i} className="truncate">{r}</li>)}
                    </ul>
                  </div>
                )}
                <Input value={notes[v.id] || ""} onChange={(e) => setNote(v.id, e.target.value)} placeholder="Note (reject reason or override justification)"
                  data-testid="org-queue-note-input"
                  className="mt-3 h-8 border-[color:var(--rb-border)] bg-[color:var(--rb-code)] text-xs text-[color:var(--rb-text)]" />
                {v.flagged && (
                  <label className="mt-2 flex items-center gap-1.5 text-xs text-[color:var(--rb-text-dim)]">
                    <input type="checkbox" checked={!!overrides[v.id]} onChange={() => toggleOverride(v.id)} data-testid="org-queue-override-checkbox" />
                    Approve anyway (override the safety scan)
                  </label>
                )}
                <div className="mt-3 flex gap-2">
                  <Button size="sm" onClick={() => approve(v)} disabled={v.flagged && !overrides[v.id]} data-testid="org-queue-approve-button"
                    className="flex-1 rounded-xl bg-[color:var(--rb-accent)] text-[color:var(--rb-void)] hover:bg-[color:var(--rb-accent-2)] disabled:opacity-40">
                    <CheckCircle2 className="mr-1.5 h-3.5 w-3.5" /> Approve
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => reject(v)} data-testid="org-queue-reject-button"
                    className="flex-1 rounded-xl border-[color:rgba(248,113,113,0.4)] text-[color:#f87171] hover:bg-[color:rgba(248,113,113,0.08)]">
                    <XCircle className="mr-1.5 h-3.5 w-3.5" /> Reject
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </AppShell>
  );
}
